"use client";

import { Badge } from "@/shadcn/ui/badge";
import Link from "next/link";
import { useShowRowContext } from "@/components/shows/ShowRowContext";

export function ShowRowBadges({ showId }: { showId: string }) {
  const {
    shows,
    isHeatingUp: rawIsHeatingUp,
    badgesLoading: loading,
    hideHeatingUp,
  } = useShowRowContext();

  const isHeatingUp = !hideHeatingUp && rawIsHeatingUp;

  const allShowsSoldOut = shows.every((show) => show.soldOut);
  const someShowsSoldOut = shows.some((show) => show.soldOut);
  const someShowsHaveLowTickets = shows.some((show) =>
    show.flags.includes("LOW_TICKETS")
  );

  if (loading) {
    // if we're loading, don't show any badges (best for layout shift to assume we won't have any)
    return null;
  }

  // first, check if there are multiple shows
  if (shows.length > 1) {
    // if all shows are sold out, show the "sold out" badge
    if (allShowsSoldOut) {
      return (
        <Badge
          variant="secondary"
          className="justify-center h-7 md:h-6 bg-[#FEF1F1] text-[#EE3233] rounded-[4px] gap-1 items-center text-xs py-[1px] px-2 w-full hover:bg-[#fbe6e6]"
        >
          Sold out!
        </Badge>
      );
    }

    // if some shows are sold out, show the "almost sold out" badge
    if (someShowsSoldOut) {
      return (
        <Badge
          variant="secondary"
          className="justify-center h-7 md:h-6 bg-[#FEF1F1] text-[#EE3233] rounded-[4px] gap-1 items-center text-xs py-[1px] px-2 w-full hover:bg-[#fbe6e6]"
        >
          Almost sold out!
        </Badge>
      );
    }

    // if some shows have low tickets, show the "low tickets" badge
    if (someShowsHaveLowTickets) {
      return (
        <Badge
          variant="secondary"
          className="justify-center h-7 md:h-6 bg-[#fbf8e7] text-[#86751A] rounded-[4px] gap-1 items-center text-xs py-[1px] px-2 w-full"
        >
          <span className="text-sm">⚠️</span>
          Low tickets!
        </Badge>
      );
    }

    if (isHeatingUp) {
      return (
        <Badge
          variant="secondary"
          className="justify-center h-7 md:h-6 bg-[#FEF1F1] text-[#EE3233] rounded-[4px] gap-1 items-center text-xs py-[1px] px-2 w-full hover:bg-[#fbe6e6]"
        >
          <span className="text-sm">🔥</span>
          Heating up!
        </Badge>
      );
    }
  }

  // we checked above if there are multiple shows, so at this point we know there's only one show
  const show = shows[0];
  const hasLowTickets = show.flags.includes("LOW_TICKETS");

  // do not render any badges if the show is sold out
  if (show.soldOut) {
    return null;
  }

  // the show is not sold out, so we need to show the "low tickets" and "heating up" badges
  if (hasLowTickets) {
    return (
      <Link
        href={`/e/${showId}`}
        className="flex flex-col items-center gap-y-2 w-full md:w-auto"
      >
        <Badge
          variant="secondary"
          className="justify-center h-7 md:h-6 bg-[#fbf8e7] text-[#86751A] rounded-[4px] gap-1 items-center text-xs py-[1px] px-2 w-full"
        >
          <span className="text-sm">⚠️</span>
          Low tickets!
        </Badge>
      </Link>
    );
  }

  if (isHeatingUp) {
    return (
      <Link
        href={`/e/${showId}`}
        className="flex flex-col items-center gap-y-2"
      >
        <Badge
          variant="secondary"
          className="justify-center h-7 md:h-6 bg-[#FEF1F1] text-[#EE3233] rounded-[4px] gap-1 items-center text-xs py-[1px] px-2 w-full hover:bg-[#fbe6e6]"
        >
          <span className="text-sm">🔥</span>
          Heating up!
        </Badge>
      </Link>
    );
  }

  // otherwise, there's no badges to show
  return null;
}
